import { useState, ChangeEvent, useEffect } from 'react';
import { Card, Table, Button, Input, Dropdown, App as AntdApp } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EllipsisVertical, RotateCcw, Search } from 'lucide-react';
import { User } from '../types';
import { useDeletedUsers, useRestoreUser } from '../hooks/useUser';
import { useAppStore } from '../store/appStore';
import UserManagementSkeleton from './Skeleton/UserManagement';
import ErrorFallback from './ErrorFallback';

const DeleteManagement = () => {
  const { message } = AntdApp.useApp();

  const [searchText, setSearchText] = useState('');
  const [filteredData, setFilteredData] = useState<User[]>([]);

  const roles = useAppStore((state) => state.roles);
  const plants = useAppStore((state) => state.plants);

  const { data: users, isLoading, error, refetch } = useDeletedUsers();
  const { mutate: restoreUser } = useRestoreUser();

  useEffect(() => {
    if (users) setFilteredData(users);
  }, [users]);

  // 🔎 Search users
  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);

    if (users) {
      const filtered = users.filter(
        (user) =>
          user.name.toLowerCase().includes(value) ||
          user.email.toLowerCase().includes(value) ||
          user.role?.roleName?.toLowerCase().includes(value) ||
          user.plant?.name?.toLowerCase().includes(value)
      );

      setFilteredData(filtered);
    }
  };

  // 🔄 Restore user
  const handleRestoreUser = (id: any) => {
    restoreUser(id, {
      onSuccess: () => {
        message.success('User restored successfully');
      },
      onError: () => {
        message.error('Failed to restore user');
      },
    });
  };

  const actionMenu = (record: User) => ({
    items: [
      {
        key: 'restore',
        label: (
          <div className="flex items-center gap-2 text-green-600">
            <RotateCcw size={16} />
            <span>Restore</span>
          </div>
        ),
        onClick: () => handleRestoreUser(record.id),
      },
    ],
  });

  const columns: ColumnsType<User> = [
    {
      title: 'Name',
      dataIndex: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Email',
      dataIndex: 'email',
    },
    {
      title: 'Phone Number',
      dataIndex: 'phoneNumber',
    },
    {
      title: 'Role',
      filters: roles.map((role: any) => ({
        text: role.roleName,
        value: role.roleName,
      })),
      onFilter: (value, record) =>
        record?.role?.roleName?.startsWith(value as string),
      render: (_, record) => record?.role?.roleName || 'N/A',
    },
    {
      title: 'Plant',
      filters: plants.map((plant: any) => ({
        text: plant.name,
        value: plant.name,
      })),
      onFilter: (value, record) =>
        record?.plant?.name?.startsWith(value as string),
      render: (_, record) => record?.plant?.name || 'N/A',
    },
    {
      title: 'Actions',
      align: 'right',
      render: (_, record) => (
        <Dropdown menu={actionMenu(record)} trigger={['click']}>
          <Button type="text" className="hover:bg-gray-100 rounded-full">
            <EllipsisVertical size={18} />
          </Button>
        </Dropdown>
      ),
    },
  ];

  if (isLoading) return <UserManagementSkeleton />;
  if (error) return <ErrorFallback error={error} refetch={refetch} />;

  return (
    <Card>
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4">
        <div className="w-full md:w-1/5 mb-4 md:mb-0">
          <Input
            placeholder="Search deleted users..."
            prefix={<Search className="w-5 h-5 text-secondary" />}
            value={searchText}
            onChange={handleSearch}
            allowClear
          />
        </div>
      </div>

      <Table<User>
        dataSource={filteredData}
        columns={columns}
        rowKey="id"
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} deleted users`,
        }}
      />
    </Card>
  );
};

export default DeleteManagement;